import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './api';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();


  // Load form data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('loginFormData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('loginFormData', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Clear login form data from localStorage on successful login
        localStorage.removeItem('loginFormData');
        
        sessionStorage.setItem('isAuth', 'true');
        sessionStorage.setItem('userRole', result.user.role);
        sessionStorage.setItem('userEmail', result.user.email);
        sessionStorage.setItem('userName', result.user.name);
        sessionStorage.setItem('userId', result.user.id);
        sessionStorage.setItem('userTable', result.user.table);
        
        // Redirect based on user role - INCLUDING DESIGNERS
        const userRole = result.user.role;
        if (userRole === 'user' || userRole === 'client') {
          navigate('/my-projects'); // Clients go to My Projects
        } else if (userRole === 'designer') {
          navigate('/designer-dashboard'); // Designers go to Designer Dashboard
        } else {
          navigate('/dashboard'); // All other roles (editors, operators, managers, admins) go to Dashboard
        }
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Cannot connect to server. Please make sure PHP server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Clear form data when leaving the page (optional)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // You can choose to keep the data or clear it when leaving
      // localStorage.removeItem('loginFormData');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // --- ENHANCED COLOR SCHEME WITH VIBRANT GRADIENTS ---
  const colors = {
    // Deep space background
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
    // Glass morphism card
    cardBg: 'rgba(255, 255, 255, 0.05)',
    // Bright, clean text
    text: '#ffffff',
    // Muted light text
    textLight: 'rgba(255, 255, 255, 0.7)',
    // Primary gradient colors
    primary: '#667eea',
    primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    // Secondary gradient colors
    secondary: '#f093fb',
    secondaryGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    // Accent colors
    accent: '#4facfe',
    accentGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    // Designer specific color
    designer: '#E91E63',
    designerGradient: 'linear-gradient(135deg, #E91E63 0%, #9C27B0 100%)',
    // Border with glow
    border: 'rgba(255, 255, 255, 0.1)',
    // Error with gradient
    error: '#ff6b6b',
    errorGradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
    // Success
    success: '#51cf66'
  };

  const [emailFocus, setEmailFocus] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);

  // Enhanced card style with glass morphism
  const cardStyle = {
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: colors.cardBg,
    borderRadius: '24px',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1),
      inset 0 -1px 0 rgba(0, 0, 0, 0.2)
    `,
  };

  // Enhanced input styles with glass effect
  const inputStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    padding: '18px 20px',
    fontSize: '16px',
    color: colors.text,
    transition: 'all 0.3s ease',
    fontFamily: 'Inter, system-ui, sans-serif',
    backdropFilter: 'blur(10px)'
  };

  const focusInputStyle = {
    ...inputStyle,
    border: `1px solid rgba(102, 126, 234, 0.6)`,
    boxShadow: `
      0 0 0 3px rgba(102, 126, 234, 0.1),
      inset 0 0 20px rgba(102, 126, 234, 0.1)
    `,
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  };

  // Enhanced button styles with animated gradient
  const buttonStyle = {
    background: colors.primaryGradient,
    border: 'none',
    borderRadius: '16px',
    color: colors.text,
    padding: '18px 32px',
    fontSize: '16px',
    fontWeight: '700',
    transition: 'all 0.4s ease',
    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)',
    position: 'relative',
    overflow: 'hidden',
    letterSpacing: '0.5px',
    backdropFilter: 'blur(10px)'
  };

  const buttonHoverStyle = {
    transform: 'translateY(-3px)',
    boxShadow: '0 15px 40px rgba(102, 126, 234, 0.6)',
    background: colors.secondaryGradient
  };

  // Infinity Logo SVG Component
  const InfinityLogo = () => (
    <svg 
      width="60" 
      height="30" 
      viewBox="0 0 100 50"
      style={{ 
        filter: 'drop-shadow(0 0 10px rgba(102, 126, 234, 0.5))'
      }}
    >
      {/* Animated Infinity Symbol */}
      <defs>
        <linearGradient id="infinityGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="50%" stopColor="#f093fb" />
          <stop offset="100%" stopColor="#4facfe" />
        </linearGradient>
        <linearGradient id="infinityGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#667eea" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#4facfe" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      
      {/* Glow Effect */}
      <path
        d="M20,25 C15,15 35,5 40,25 C45,45 65,35 80,25 C85,15 65,5 60,25 C55,45 35,35 20,25 Z"
        fill="url(#infinityGlow)"
        opacity="0.3"
        style={{
          animation: 'infinityPulse 3s ease-in-out infinite'
        }}
      />
      
      {/* Main Infinity Symbol */}
      <path
        d="M20,25 C15,15 35,5 40,25 C45,45 65,35 80,25 C85,15 65,5 60,25 C55,45 35,35 20,25 Z"
        fill="none"
        stroke="url(#infinityGradient)"
        strokeWidth="3"
        strokeLinecap="round"
        style={{
          animation: 'infinityDraw 2s ease-in-out infinite'
        }}
      />
      
      {/* Animated Dots */}
      <circle cx="30" cy="25" r="2" fill="#667eea" style={{ animation: 'dotMove 4s linear infinite' }} />
      <circle cx="70" cy="25" r="2" fill="#4facfe" style={{ animation: 'dotMove 4s linear infinite 2s' }} />
    </svg>
  );

  return (
    <div 
      className="login-page-container"
      style={{ 
        background: colors.background,
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}
    >
      
      {/* Animated Background Elements */}
      <div 
        className="login-background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: colors.background,
          pointerEvents: 'none'
        }}
      ></div>

      {/* Animated Orbs */}
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="login-orb"
          style={{
            position: 'absolute',
            width: `${Math.random() * 300 + 100}px`,
            height: `${Math.random() * 300 + 100}px`,
            background: i % 2 === 0 ? colors.primaryGradient : colors.secondaryGradient,
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `login-orb-float ${Math.random() * 30 + 20}s infinite ease-in-out`,
            animationDelay: `${Math.random() * 10}s`,
            filter: 'blur(40px)',
            opacity: 0.15,
            pointerEvents: 'none'
          }}
        />
      ))}

      {/* Grid Pattern */}
      <div 
        className="login-grid"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          opacity: 0.3,
          pointerEvents: 'none'
        }}
      ></div>

      {/* Main Card */}
      <div className="login-card-wrapper" style={{ width: '100%', maxWidth: '440px', padding: '0 20px', zIndex: 10 }}>
        <div 
          className="login-card"
          style={cardStyle}
        >
          
          {/* Animated Border Glow */}
          <div 
            className="login-card-glow"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(45deg, transparent, ${colors.primary}20, ${colors.secondary}20, transparent)`,
              borderRadius: '24px',
              animation: 'login-glow-rotate 8s linear infinite',
              pointerEvents: 'none',
              opacity: 0.6
            }}
          ></div>
          
          {/* Header */}
          <div 
            className="login-header"
            style={{ 
              textAlign: 'center', 
              padding: '50px 40px 30px 40px',
              border: 'none',
              background: 'transparent'
            }}
          >
            <div 
              className="login-logo-container"
              style={{ marginBottom: '30px' }}
            >
              <div 
                className="login-logo"
                style={{
                  width: '120px',
                  height: '120px',
                  margin: '0 auto 20px auto',
                  background: colors.primaryGradient,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `
                    0 15px 35px rgba(102, 126, 234, 0.4),
                    inset 0 2px 0 rgba(255, 255, 255, 0.2),
                    inset 0 -2px 0 rgba(0, 0, 0, 0.1)
                  `,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Infinity Logo */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%'
                }}>
                  <InfinityLogo />
                </div>
                
                {/* Logo Shine Effect */}
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'login-logo-shine 3s ease-in-out infinite',
                    borderRadius: '50%'
                  }}
                ></div>

                {/* Rotating Ring */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '-5px',
                    left: '-5px',
                    right: '-5px',
                    bottom: '-5px',
                    border: '2px solid transparent',
                    borderTop: '2px solid #667eea',
                    borderRight: '2px solid #f093fb',
                    borderRadius: '50%',
                    animation: 'logo-ring-rotate 3s linear infinite'
                  }}
                ></div>
              </div>
            </div>
            <h2 
              className="login-title"
              style={{ 
                color: colors.text,
                fontSize: '32px',
                fontWeight: '800',
                marginBottom: '8px',
                letterSpacing: '-0.5px',
                background: colors.primaryGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              AlphaTrack
            </h2>
            <p 
              className="login-subtitle"
              style={{ 
                color: colors.textLight,
                fontSize: '16px',
                margin: 0,
                fontWeight: '400'
              }}
            >
              Welcome back! Please sign in to continue.
            </p>
          </div>
          
          {/* Body */}
          <div 
            className="login-body"
            style={{ padding: '0 40px 40px 40px' }}
          >
            {error && (
              <div 
                className="login-error"
                style={{ 
                  background: 'rgba(255, 107, 107, 0.1)',
                  borderRadius: '12px',
                  color: colors.error,
                  fontSize: '14px',
                  padding: '14px 16px',
                  textAlign: 'left',
                  marginBottom: '24px',
                  animation: 'login-shake 0.5s ease-in-out',
                  fontWeight: '500',
                  border: `1px solid rgba(255, 107, 107, 0.3)`,
                  display: 'block',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <i className="fas fa-exclamation-circle" style={{ marginRight: '10px' }}></i>
                {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              {/* Email Field */}
              <div style={{ marginBottom: '24px' }}>
                <label 
                  className="login-label"
                  style={{ 
                    color: colors.text,
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '12px',
                    display: 'block'
                  }}
                >
                  Email Address
                </label>
                <div style={{ display: 'flex', position: 'relative' }}>
                  <span 
                    className="login-input-icon"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${emailFocus ? 'rgba(102, 126, 234, 0.6)' : colors.border}`,
                      borderRight: 'none',
                      borderTopLeftRadius: '16px',
                      borderBottomLeftRadius: '16px',
                      padding: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      color: emailFocus ? colors.primary : colors.textLight,
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <i className="fas fa-envelope"></i>
                  </span>
                  <input
                    type="email"
                    className="login-input"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                    style={{
                      ...(emailFocus ? focusInputStyle : inputStyle),
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      width: '100%'
                    }}
                    onFocus={() => setEmailFocus(true)}
                    onBlur={() => setEmailFocus(false)}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: '32px' }}>
                <label 
                  className="login-label"
                  style={{ 
                    color: colors.text,
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '12px',
                    display: 'block'
                  }}
                >
                  Password
                </label>
                <div style={{ display: 'flex', position: 'relative' }}>
                  <span 
                    className="login-input-icon"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${passwordFocus ? 'rgba(102, 126, 234, 0.6)' : colors.border}`,
                      borderRight: 'none',
                      borderTopLeftRadius: '16px',
                      borderBottomLeftRadius: '16px',
                      padding: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      color: passwordFocus ? colors.primary : colors.textLight,
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <i className="fas fa-lock"></i>
                  </span>
                  <input
                    type="password"
                    className="login-input"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your password"
                    style={{
                      ...(passwordFocus ? focusInputStyle : inputStyle),
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      width: '100%'
                    }}
                    onFocus={() => setPasswordFocus(true)}
                    onBlur={() => setPasswordFocus(false)}
                  />
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className="login-button"
                style={buttonHover ? { ...buttonStyle, ...buttonHoverStyle } : buttonStyle}
                disabled={loading}
                onMouseEnter={() => setButtonHover(true)}
                onMouseLeave={() => setButtonHover(false)}
              >
                {loading ? (
                  <>
                    <span 
                      className="login-spinner"
                      style={{
                        display: 'inline-block',
                        width: '18px',
                        height: '18px',
                        border: '2px solid transparent',
                        borderTop: '2px solid currentColor',
                        borderRadius: '50%',
                        animation: 'login-spin 1s linear infinite',
                        marginRight: '10px'
                      }}
                    ></span>
                    Signing In...
                  </>
                ) : (
                  <>
                    <i className="fas fa-rocket" style={{ marginRight: '10px' }}></i>
                    Launch Dashboard
                  </>
                )}
                
                {/* Button Shine Effect */}
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s ease',
                    borderRadius: '16px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.left = '100%';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.left = '-100%';
                  }}
                ></div>
              </button>
            </form>
          </div>

          {/* Footer */}
          <div 
            className="login-footer"
            style={{ 
              background: 'rgba(0, 0, 0, 0.2)',
              borderTop: `1px solid ${colors.border}`,
              textAlign: 'center',
              padding: '24px 40px',
              border: 'none',
              borderBottomLeftRadius: '24px',
              borderBottomRightRadius: '24px',
              backdropFilter: 'blur(10px)'
            }}
          >
            <small style={{ color: colors.textLight, fontSize: '12px', opacity: 0.7 }}>
              © 2025 AlphaTrack • Secure Access System
            </small>
          </div>
        </div>
      </div>

      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes login-orb-float {
          0%, 100% {
            transform: translateY(0) translateX(0) scale(1) rotate(0deg);
            opacity: 0.1;
          }
          33% {
            transform: translateY(-40px) translateX(20px) scale(1.1) rotate(120deg);
            opacity: 0.2;
          }
          66% {
            transform: translateY(20px) translateX(-30px) scale(0.9) rotate(240deg);
            opacity: 0.15;
          }
        }
        
        @keyframes login-glow-rotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        @keyframes login-logo-shine {
          0%, 100% {
            left: -100%;
          }
          50% {
            left: 100%;
          }
        }
        
        @keyframes logo-ring-rotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        @keyframes infinityDraw {
          0%, 100% {
            stroke-dasharray: 100 100;
            stroke-dashoffset: 0;
          }
          50% {
            stroke-dasharray: 150 150;
            stroke-dashoffset: 50;
          }
        }
        
        @keyframes infinityPulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.05);
          }
        }
        
        @keyframes dotMove {
          0% {
            transform: translateX(0) translateY(0);
            opacity: 0;
          }
          25% {
            transform: translateX(10px) translateY(-5px);
            opacity: 1;
          }
          50% {
            transform: translateX(0) translateY(0);
            opacity: 0;
          }
          75% {
            transform: translateX(-10px) translateY(5px);
            opacity: 1;
          }
          100% {
            transform: translateX(0) translateY(0);
            opacity: 0;
          }
        }
        
        @keyframes login-shake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-8px);
          }
          75% {
            transform: translateX(8px);
          }
        }
        
        @keyframes login-spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        .login-card {
          animation: login-appear 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        @keyframes login-appear {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        /* Ensure no external styles affect login page */
        .login-page-container * {
          box-sizing: border-box !important;
        }
        
        .login-input::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
        
        .login-input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        
        .login-button:disabled {
          opacity: 0.6 !important;
          transform: none !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </div>
  );
}

export default Login;